import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  AdminChatConversationResponse,
  AdminChatInboxEntry,
  AdminChatInboxResponse,
  AdminChatMessageView,
  AdminChatOnlineRosterResponse,
} from '@kopechka/contracts';
import {
  getAdminChatInbox,
  getAdminChatConversation,
  getAdminChatOnline,
  listAdminUsers,
  markAdminChatRead,
  sendAdminChatMessage,
  type AdminUserListResponse,
} from '../api/endpoints.js';
import { resolveBaseUrl } from '../api/client.js';
import { useApiQuery, useDebouncedValue, useVisiblePolling } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import { SupportPanelKeysCard } from './chat/SupportPanelKeysCard.js';
import { SoftBreaks } from './user-inspect/SoftBreaks.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Modal,
  PageHead,
  Textarea,
} from '../ui/index.js';
import '../styles/messages.mobile.css';

const POLL_MS = 5000;

/**
 * `/messages` — admin↔member support inbox (D-231). One screen of every member
 * who has a conversation (left), the selected thread + reply composer (right).
 * Reads come from the chat projection read-models; presence (online / last
 * active) is enriched from Redis liveness. Gated on `chat:view`; reply needs
 * `chat:send`.
 */
export function ChatInboxPage(): JSX.Element {
  const canSend = useCan('chat:send');
  const inbox = useApiQuery<AdminChatInboxResponse>((o) => getAdminChatInbox(o), []);
  const [searchParams] = useSearchParams();
  // Deep-link from the user-inspect page: /messages?actor=<actorId> preselects.
  const [selected, setSelected] = useState<string | null>(
    () => searchParams.get('actor'),
  );
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeOnlineOnly, setComposeOnlineOnly] = useState(false);

  // Active-now count — cheap ZCOUNT poll for the "kaç kişi online" header badge.
  const online = useApiQuery<AdminChatOnlineRosterResponse>(
    (o) => getAdminChatOnline(o, false),
    [],
  );
  const reloadOnline = online.reload;

  // Poll the inbox list + the online count every 5s.
  const reloadInbox = inbox.reload;
  useVisiblePolling(() => { reloadInbox(); reloadOnline(); }, POLL_MS);

  const conversations: readonly AdminChatInboxEntry[] =
    inbox.kind === 'ok' ? inbox.data.conversations : [];
  const onlineCount = online.kind === 'ok' && !online.refreshError && online.data.presenceAvailable !== false ? online.data.onlineCount : null;

  // Phones/tablets (master-detail, see isMasterDetail) show the list OR the
  // thread. Keep the operator's place in the list so "← Geri" returns to the
  // conversation they opened instead of the top of the page. Presentation only.
  const chatRef = useRef<HTMLDivElement | null>(null);
  const listScrollRef = useRef<number | null>(null);
  function openConversation(conversationId: string): void {
    listScrollRef.current = isMasterDetail(chatRef.current) ? window.scrollY : null;
    setSelected(conversationId);
  }
  useEffect(() => {
    if (selected !== null) return;
    const y = listScrollRef.current;
    listScrollRef.current = null;
    if (y !== null && isMasterDetail(chatRef.current)) window.scrollTo(0, y);
  }, [selected]);

  function openCompose(onlineOnly: boolean): void {
    setComposeOnlineOnly(onlineOnly);
    setComposeOpen(true);
  }
  function pickUser(actorId: string): void {
    setSelected(actorId);
    setComposeOpen(false);
  }

  return (
    <div className="kop-messages-page">
      <PageHead
        crumb={<>Müşteri <Icon.ChevronRight size={11} /> Mesajlar</>}
        title="Mesajlar"
        subtitle="Üyelerle birebir destek sohbeti. Bir üyeye siz de yazabilirsiniz."
        actions={
          canSend ? (
            <div className="kop-chat-actions" style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
              <button
                type="button"
                className="kop-online-pill"
                onClick={() => openCompose(true)}
                title="Çevrim içi üyeleri gör ve yaz"
              >
                <PresenceDot online={onlineCount !== null && onlineCount > 0} available={onlineCount !== null} />
                Çevrim içi: <strong>{onlineCount ?? '—'}</strong>
              </button>
              <Button variant="primary" icon={<Icon.Mail size={14} />} onClick={() => openCompose(false)}>
                Yeni mesaj
              </Button>
            </div>
          ) : null
        }
      />
      {(inbox.refreshError || online.refreshError || (online.kind === 'ok' && online.data.presenceAvailable === false)) && <Banner tone="warn">Aktiflik güncellenemedi. Son mesajlar korunuyor; çevrim içi durumu bilinmiyor.</Banner>}
      {composeOpen && (
        <ComposePicker
          onlineOnly={composeOnlineOnly}
          onPick={pickUser}
          onClose={() => setComposeOpen(false)}
        />
      )}
      <div className="kop-admin-chat" data-view={selected ? 'thread' : 'list'} ref={chatRef}>
        <Card flush>
          <CardBody>
            {inbox.kind === 'loading' && conversations.length === 0 ? (
              <LoadingState label="Konuşmalar yükleniyor…" />
            ) : inbox.kind === 'error' ? (
              <ErrorState reason={inbox.reason} onRetry={() => inbox.reload()} />
            ) : conversations.length === 0 ? (
              <EmptyState label="Henüz mesaj yok" hint="Bir üye yazdığında burada görünecek." />
            ) : (
              <ul className="kop-admin-chat-list">
                {conversations.map((c) => (
                  <li key={c.conversationId}>
                    <button
                      type="button"
                      className={
                        selected === c.conversationId
                          ? 'kop-admin-chat-item kop-admin-chat-item--active'
                          : 'kop-admin-chat-item'
                      }
                      onClick={() => openConversation(c.conversationId)}
                    >
                      <span className="kop-admin-chat-item-top">
                        <span className="kop-admin-chat-item-name">
                          <PresenceDot online={!inbox.refreshError && c.presence.presenceAvailable !== false && c.presence.isOnline} available={!inbox.refreshError && c.presence.presenceAvailable !== false} />
                          <span className="kop-admin-chat-item-label" title={c.customerEmail ?? c.customerActorId}>
                            {c.customerEmail ?? c.customerActorId}
                          </span>
                        </span>
                        {c.unreadForAdmin > 0 && (
                          <Badge tone="danger">{c.unreadForAdmin}</Badge>
                        )}
                      </span>
                      <span className="kop-admin-chat-item-preview">
                        {c.lastSenderRole === 'admin' ? 'Siz: ' : ''}
                        {c.lastMessagePreview ?? '—'}
                      </span>
                      <span className="kop-admin-chat-item-meta">
                        {relativeTime(c.lastMessageAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card flush>
          <CardBody>
            {selected ? (
              <ConversationPane
                key={selected}
                actorId={selected}
                canSend={canSend}
                onSent={reloadInbox}
                onReadCleared={reloadInbox}
                onBack={() => setSelected(null)}
              />
            ) : (
              <EmptyState label="Bir konuşma seçin" hint="Soldaki listeden bir üye seçin." />
            )}
          </CardBody>
        </Card>
      </div>
      {/* Support-panel integration keys (chat:integrations). Below the grid so it
          never shifts the inbox/thread layout; it does not join the 5 s poll. */}
      <SupportPanelKeysCard />
    </div>
  );
}

/**
 * Compose / online-roster picker. Search any member (live) or filter to who's
 * online now, then pick one to start (or continue) a conversation. Reuses
 * listAdminUsers (search + isOnlineNow + presence) — one modal for both the
 * "Yeni mesaj" and "Çevrim içi" entry points.
 */
function ComposePicker({
  onlineOnly,
  onPick,
  onClose,
}: {
  readonly onlineOnly: boolean;
  readonly onPick: (actorId: string) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const [onlyOnline, setOnlyOnline] = useState(onlineOnly);
  const search = useDebouncedValue(draft, 300);
  const q = useApiQuery<AdminUserListResponse>(
    (o) =>
      listAdminUsers(o, {
        page: 1,
        limit: 30,
        search,
        status: 'all',
        role: 'user',
        sortBy: onlyOnline ? 'isOnlineNow' : 'createdAt',
        sortOrder: 'desc',
      }),
    [search, onlyOnline],
  );
  useVisiblePolling(q.reload, POLL_MS);
  const rows = q.kind === 'ok' ? q.data.data.users : [];
  const presenceUnavailable = !!q.refreshError || rows.some((u) => u.presenceAvailable === false);
  const filtered = onlyOnline ? rows.filter((u) => u.presenceAvailable !== false && u.isOnlineNow) : rows;
  return (
    <Modal open onClose={onClose} title="Üye seç ve yaz">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, width: '100%' }}>
        <Input
          type="search"
          placeholder="E-posta / kullanıcı adı ara…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          iconLeft={<Icon.Search size={14} />}
          autoFocus
        />
        <label className="kop-compose-filter">
          <input
            type="checkbox"
            checked={onlyOnline}
            onChange={(e) => setOnlyOnline(e.target.checked)}
          />
          Sadece çevrim içi
        </label>
        {presenceUnavailable && <Banner tone="warn">Aktiflik bilgisi alınamıyor. Çevrim içi filtre sonuçları bilinmiyor.</Banner>}
        {q.kind === 'error' && <ErrorState reason={q.reason} onRetry={q.reload} />}
        <div className="kop-compose-list">
          {q.kind === 'loading' ? (
            <LoadingState label="Yükleniyor…" />
          ) : filtered.length === 0 ? (
            <EmptyState label={onlyOnline && presenceUnavailable ? "Çevrim içi bilgisi şu anda alınamıyor." : "Eşleşen üye yok."} />
          ) : (
            filtered.map((u) => (
              <button
                key={u.actorId}
                type="button"
                className="kop-compose-row"
                onClick={() => onPick(u.actorId)}
              >
                <span className="kop-compose-row-main">
                  <PresenceDot online={!q.refreshError && u.presenceAvailable !== false && u.isOnlineNow} available={!q.refreshError && u.presenceAvailable !== false} />
                  <span className="kop-compose-row-label" title={u.email ?? u.username ?? u.actorId}>
                    {u.email ?? u.username ?? u.actorId}
                  </span>
                </span>
                <span className="kop-compose-row-meta">
                  {q.refreshError || u.presenceAvailable === false ? 'bilinmiyor' : u.isOnlineNow ? 'çevrim içi' : relativeTime(u.lastActiveAt)}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

function ConversationPane({
  actorId,
  canSend,
  onSent,
  onReadCleared,
  onBack,
}: {
  readonly actorId: string;
  readonly canSend: boolean;
  readonly onSent: () => void;
  readonly onReadCleared: () => void;
  readonly onBack: () => void;
}): JSX.Element {
  const [data, setData] = useState<AdminChatConversationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Optimistically-shown messages the admin just sent, before the projection
  // read-model catches up (the projection lags a few seconds). Reconciled on
  // each load() — dropped once the authoritative read-model contains them.
  const [pending, setPending] = useState<AdminChatMessageView[]>([]);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const paneRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef<HTMLDivElement | null>(null);
  const requestRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const markedRef = useRef(false);
  // Synchronous in-flight guard — blocks a double Enter/click before the
  // `sending` state re-render disables the button (compose first-send safety).
  const inFlightRef = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    if (controllerRef.current) return;
    const sequence = ++requestRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
    const res = await getAdminChatConversation({ baseUrl: resolveBaseUrl(), signal: controller.signal }, actorId);
    if (controller.signal.aborted || sequence !== requestRef.current) return;
    if (res.kind === 'ok') {
      setData(res.data);
      setError(null);
      // Drop optimistic messages the read-model now contains.
      const ids = new Set(res.data.messages.map((m) => m.id));
      setPending((prev) => prev.filter((m) => !ids.has(m.id)));
      // Clear the admin unread badge once per opened thread.
      if (!markedRef.current) {
        markedRef.current = true;
        // A slow read-receipt write must not hold the presence read open.
        void markAdminChatRead({ baseUrl: resolveBaseUrl(), signal: controller.signal }, actorId).then(marked => {
          if (controller.signal.aborted || sequence !== requestRef.current) return;
          if (marked.kind === 'ok') onReadCleared();
          else markedRef.current = false;
        }).catch(() => {
          if (sequence === requestRef.current) markedRef.current = false;
        });
      }
    } else if (res.kind === 'forbidden') {
      setError('forbidden');
    } else {
      setError('reason' in res ? res.reason : 'error');
    }
    setLoading(false);
    } catch {
      if (controller.signal.aborted || sequence !== requestRef.current) return;
      setError('network_error');
      setLoading(false);
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  }, [actorId, onReadCleared]);

  // Initial load; polling/focus only refresh visible views.
  useEffect(() => {
    void load();
    return () => { ++requestRef.current; controllerRef.current?.abort(); controllerRef.current = null; };
  }, [load]);
  useVisiblePolling(load, POLL_MS);

  useEffect(() => {
    const messages = messagesRef.current;
    if (messages) messages.scrollTop = messages.scrollHeight;
  }, [data?.messages.length, pending.length]);

  // The thread pane (header, log, composer) is rendered; otherwise the
  // loading / error card stands in for it (see the returns below).
  const paneShown = !(loading && !data) && error !== 'forbidden' && !(error && !data);

  // Phones/tablets (master-detail): the pane sits under the alarm banner + page
  // head, so the reply box started below the fold. Bring the opened thread
  // (header, log, composer) into view once, under the sticky top bar
  // (scroll-margin in messages.mobile.css). Declared before the height effect
  // below so that measures the pane at its revealed position. The pane is
  // sized for that position first: with only its CSS floor height a short
  // page (hard load, tablet) stopped the scroll part-way, leaving slivers of
  // the page actions under the top bar.
  const revealedRef = useRef(false);
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane || revealedRef.current) return;
    revealedRef.current = true;
    if (!isMasterDetail(pane.closest('.kop-admin-chat')) || typeof pane.scrollIntoView !== 'function') return;
    const target = parseFloat(getComputedStyle(pane).scrollMarginTop) || 0;
    pane.style.setProperty('--kop-chat-available-height', `${Math.max(180, Math.floor(viewportBottom() - target - 16))}px`);
    pane.scrollIntoView({ block: 'start' });
  });

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    // Where the pane sits on the page / on screen, for the layout watcher below.
    let pageTop = 0;
    let screenTop = 0;
    let onScreen = false;
    const track = () => {
      const rect = pane.getBoundingClientRect();
      pageTop = Math.round(rect.top + window.scrollY);
      screenTop = Math.round(rect.top);
      onScreen = rect.top >= 0 && rect.bottom <= viewportBottom() + 1;
    };
    const resize = () => {
      if (!isMasterDetail(pane.closest('.kop-admin-chat'))) { pane.style.removeProperty('--kop-chat-available-height'); track(); return; }
      const available = Math.max(180, Math.floor(viewportBottom() - Math.max(0, pane.getBoundingClientRect().top) - 16));
      pane.style.setProperty('--kop-chat-available-height', `${available}px`);
      track();
    };
    // Content above the pane that renders after it (the global alarm banner
    // on a hard load / deep link, a warning banner) moves it down the page
    // without resizing its parent, which left the reply box below the fold.
    // Re-measure then; a pane that was fully on screen after the page had
    // been scrolled stays where it was (what Chrome's scroll anchoring does,
    // for browsers without it). Presentation only.
    const relayout = () => {
      const rect = pane.getBoundingClientRect();
      if (Math.round(rect.top + window.scrollY) === pageTop) return;
      if (onScreen && window.scrollY > 0 && isMasterDetail(pane.closest('.kop-admin-chat'))) {
        const shift = Math.round(rect.top) - screenTop;
        if (shift !== 0) window.scrollBy(0, shift);
      }
      resize();
    };
    resize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (pane.parentElement) observer?.observe(pane.parentElement);
    // The shell's <main> grows with the page (html/body are fixed at 100%).
    const page = pane.closest('main') ?? pane.closest('.kop-messages-page')?.parentElement;
    const pageObserver = page && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(relayout) : null;
    if (page) pageObserver?.observe(page);
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', track, { passive: true });
    window.visualViewport?.addEventListener('resize', resize);
    return () => {
      observer?.disconnect();
      pageObserver?.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', track);
      window.visualViewport?.removeEventListener('resize', resize);
    };
  }, [paneShown]);

  async function send(): Promise<void> {
    const body = draft.trim();
    if (!body || sending || inFlightRef.current) return;
    inFlightRef.current = true;
    setSending(true);
    try {
      const res = await sendAdminChatMessage({ baseUrl: resolveBaseUrl() }, actorId, body);
      if (res.kind === 'ok') {
        setDraft('');
        setSendError(null);
        // Show it instantly; load() reconciles when the projection catches up.
        setPending((prev) => [...prev, res.data.message]);
        await load();
        onSent();
      } else if (res.kind === 'forbidden') {
        setSendError('Yetki yok (chat:send).');
      } else {
        setSendError('reason' in res ? res.reason : 'Gönderilemedi.');
      }
    } finally {
      inFlightRef.current = false;
      setSending(false);
    }
  }

  // Master-detail: a thread that is still loading or failed shows its card in
  // the pane's place, under the page head; on a landscape phone only "← Geri"
  // was on screen, the card itself below the fold. Bring it into view once,
  // like the pane (scroll-margin in messages.mobile.css). View only.
  useEffect(() => {
    const el = stateRef.current;
    if (!el || !isMasterDetail(el.closest('.kop-admin-chat')) || typeof el.scrollIntoView !== 'function') return;
    const rect = el.getBoundingClientRect();
    const clearTop = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    if (rect.top >= clearTop && rect.bottom <= viewportBottom()) return;
    el.scrollIntoView({ block: 'start' });
  }, [paneShown]);

  // "← Geri" (master-detail only, hidden on desktop by CSS). In master-detail
  // the list is hidden while a thread is selected, so the loading and error
  // cards carry it too: a hung or failed thread must not trap the operator.
  const backButton = (
    <button
      type="button"
      className="kop-admin-chat-back"
      aria-label="Konuşma listesine dön"
      onClick={onBack}
    >
      ← Geri
    </button>
  );
  if (!paneShown) {
    return (
      <div className="kop-admin-chat-pane-state" ref={stateRef}>
        {backButton}
        {loading && !data ? (
          <LoadingState label="Konuşma yükleniyor…" />
        ) : error === 'forbidden' ? (
          <ErrorState reason="Yetki yok (chat:view)." />
        ) : (
          <ErrorState reason={error ?? 'error'} onRetry={() => void load()} />
        )}
      </div>
    );
  }

  return (
    <div className="kop-admin-chat-pane" ref={paneRef}>
      <header className="kop-admin-chat-pane-head">
        {backButton}
        <span className="kop-admin-chat-pane-name" title={data?.customerEmail ?? actorId}>
          <PresenceDot online={!error && data?.presence.presenceAvailable !== false && (data?.presence.isOnline ?? false)} available={!!data && !error && data.presence.presenceAvailable !== false} />
          <span className="kop-admin-chat-identity"><SoftBreaks text={data?.customerEmail ?? actorId} /></span>
        </span>
        <span className="kop-admin-chat-pane-presence">
          {error || data?.presence.presenceAvailable === false ? 'Aktiflik bilinmiyor' : data?.presence.isOnline
            ? 'Çevrimiçi'
            : `${data?.presence.lastActiveSource === 'last_login' ? 'Son giriş kaydı' : 'Son aktif'}: ${relativeTime(data?.presence.lastActiveAt ?? null)}`}
        </span>
      </header>
      {error && <Banner tone="warn">Konuşma güncellenemedi. Son mesajlar gösteriliyor.</Banner>}

      <div className="kop-admin-chat-messages" role="log" aria-live="polite" ref={messagesRef}>
        {(() => {
          const base = data?.messages ?? [];
          const baseIds = new Set(base.map((m) => m.id));
          const merged = [...base, ...pending.filter((m) => !baseIds.has(m.id))];
          if (merged.length === 0) {
            return <p className="kop-admin-chat-empty">Bu konuşmada henüz mesaj yok.</p>;
          }
          return merged.map((m) => (
            <div
              key={m.id}
              className={
                m.senderRole === 'admin'
                  ? 'kop-admin-chat-msg kop-admin-chat-msg--admin'
                  : 'kop-admin-chat-msg kop-admin-chat-msg--customer'
              }
            >
              <span className="kop-admin-chat-bubble">{m.body}</span>
              <time className="kop-admin-chat-time" dateTime={m.createdAt}>
                {formatTime(m.createdAt)}
              </time>
            </div>
          ));
        })()}
      </div>

      {sendError && (
        <div style={{ padding: '0 4px 6px' }}>
          <Banner tone="danger">{sendError}</Banner>
        </div>
      )}
      <div className="kop-admin-chat-composer">
        <Textarea
          rows={2}
          value={draft}
          placeholder={canSend ? 'Yanıt yazın…' : 'Yanıtlama yetkiniz yok (chat:send).'}
          disabled={!canSend || sending}
          maxLength={4000}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <Button
          variant="primary"
          loading={sending}
          disabled={!canSend || draft.trim().length === 0}
          onClick={() => void send()}
        >
          Gönder
        </Button>
      </div>
    </div>
  );
}

/**
 * The inbox is master-detail (list OR thread) at ≤900px (styles.css chat
 * block) and whenever the chat area itself is narrower than 720px — a tablet
 * with the sidebar open (912, 1024), see the container query in
 * styles/messages.mobile.css. Presentation only.
 */
const CHAT_MASTER_DETAIL_MAX = 720;

/** Bottom edge of the visible viewport (shrinks with the on-screen keyboard). */
function viewportBottom(): number {
  const viewport = window.visualViewport;
  return (viewport?.height ?? window.innerHeight) + (viewport?.offsetTop ?? 0);
}
function isMasterDetail(chat: Element | null | undefined): boolean {
  if (typeof window === 'undefined') return false;
  if (window.innerWidth <= 900) return true;
  const width = chat instanceof HTMLElement ? chat.clientWidth : 0;
  return width > 0 && width < CHAT_MASTER_DETAIL_MAX;
}

function PresenceDot({ online, available = true }: { readonly online: boolean; readonly available?: boolean }): JSX.Element {
  return (
    <span
      className={online ? 'kop-presence-dot kop-presence-dot--on' : 'kop-presence-dot'}
      title={available ? online ? 'Çevrim içi' : 'Çevrim dışı' : 'Aktiflik bilinmiyor'}
      aria-hidden="true"
    />
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Compact Turkish relative time for the inbox + presence line. */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'Kayıt yok';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Kayıt yok';
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} gün önce`;
  return d.toLocaleDateString();
}
